
import React from "react";
import ReactDOM from "react-dom/client";
import * as Hooks from "../../../../../hooks";
import { i18nManager } from "../../../../../i18n";
import { Schemes, Utils } from "../../../../../utils";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";
import { BoolHelper, Button, Checkbox, DOMHelper, Dropdown, Form, FormControl, NumberInput, ObjectHelper, Scheme, StringHelper, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import { BaseService, ConsusService, ITournamentEdition, ITournamentEditionPrize, IUploadedFile, SanitizedResponse, TournamentPrizeType, TournamentService } from "../../../../../services";
import { CreateTournamentSectionProps, CreateTournamentSection } from "../CreateTournament";
import MediaInput from "../../../../shared/MediaInput";
import { Elements } from "../../../../shared/Elements";

export interface AddOrEditTournamentPrizeProps {
    prizeCountries: string[];
    type: "TOP_WINNER" | "TROPHY";
    fetchInformations: () => void;
    prize?: ITournamentEditionPrize;
    tournamentEdition: Partial<ITournamentEdition>;
    setActiveSection?: (section: CreateTournamentSection, done?: () => void, noSave?: boolean) => void;
}

export function AddOrEditTournamentPrize(props: AddOrEditTournamentPrizeProps) {
    const { prizeCountries, type, prize = {} as ITournamentEditionPrize, tournamentEdition, fetchInformations } = props;
    let title: string;
    prize.type = type;
    const isNew = !prize?.external_id;
    const dialogErrorRef = { current: null } as any;
    const consusService = ConsusService.getInstance();
    const width = document.documentElement.clientWidth;
    const mediaInputManageRef = { current: undefined as any };
    const tournamentService = TournamentService.getInstance();
    const physicalMediaInputManageRef = { current: undefined as any };
    const labels = i18nManager.Labels.host.create_tournament.prize_pool;
    const optionsMutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;
    const secondaryOptionsMutableRoot = { current: null } as unknown as React.RefObject<ReactDOM.Root>;
    const dialogLabels = labels.dialog.manage_prize;

    function buildMonetaryPrizeComponents() {
        const isMonetaryAndPhysical = prize.prize_type === TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE;
        return (<Form scheme={Schemes.OMNEHOST} style={{
            marginTop: 20,
            paddingTop: 20,
            height: "100%",
            paddingLeft: (isMonetaryAndPhysical ? 20 : 0),
            [isMonetaryAndPhysical ? `borderLeft` : "borderTop"]: "1px solid #E3E3E3",
        }}>
            {prizeCountries.map((prizeCountry) => {
                const countryName = i18nManager.getCountryName(prizeCountry);
                const currency = i18nManager.getCountryCurrency(prizeCountry);
                const currencySign = i18nManager.getCountryCurrencySign(prizeCountry);

                return (<FormControl key={prizeCountry} isFieldset fill label={`${countryName} (${currency})`} style={{ flex: 1, marginBottom: 15 }}
                    leftContent={<span>{currencySign}</span>}>
                    <NumberInput fill defaultValue={(prize.currency_prizes?.[currency] ?? 0) / 1000000} onInputComplete={(value: string) => {
                        if (!prize.currency_prizes) prize.currency_prizes = {};
                        prize.currency_prizes[currency] = parseFloat(value) * 1000000;
                    }} />
                </FormControl>);
            })}
            <span>{dialogLabels.leave_empty_for_no_prize}</span>
        </Form>);
    }

    function buildPhysicalPrizeComponents() {
        if (!prize.physical_prize) prize.physical_prize = {} as any;
        return (<Form scheme={Schemes.OMNEHOST} style={{ borderTop: "1px solid #E3E3E3", marginTop: 20, paddingTop: 20 }}>
            <FormControl isFieldset fill label={(<label>{dialogLabels.name} <span style={{ color: "#FF7171" }}>*</span></label>)} style={{ flex: 1, marginBottom: 15 }}>
                <TextInput fill defaultValue={prize.physical_prize?.name} onInputComplete={(value: string) => {
                    prize.physical_prize!.name = value;
                }} />
            </FormControl>
            <MediaInput manageRef={physicalMediaInputManageRef} width={"100%"} height={180} instruction={dialogLabels.select_prize_image}
                default={prize.physical_prize?.image_url} onSelectFiles={(_) => dialogErrorRef.current!.innerText = " "} onRemove={async () => {
                    // TODO delete on consus
                }} />
            <FormControl isFieldset fill label={(<label>{dialogLabels.how_to_claim} <span style={{ color: "#FF7171" }}>*</span></label>)} style={{ flex: 1, marginBottom: 15 }}>
                <TextAreaInput fill style={{ height: 100 }} defaultValue={prize.physical_prize?.how_to_claim} onInputComplete={(value: string) => {
                    prize.physical_prize!.how_to_claim = value;
                }} />
            </FormControl>
            <FormControl isFieldset fill label={dialogLabels.detail_link} style={{ flex: 1, marginBottom: 15 }}>
                <TextInput fill defaultValue={prize.physical_prize?.detail_link} onInputComplete={(value: string) => {
                    prize.physical_prize!.detail_link = value;
                }} />
            </FormControl>
            <Checkbox style={{ marginTop: 20 }} label={dialogLabels.avail_in_all_countries} defaultChecked={prize.physical_prize?.available_in_all_countries} onChecked={(v) => {
                prize.physical_prize!.available_in_all_countries = v;
            }} />
        </Form>);
    }

    function renderPrizeComponents() {
        if (!optionsMutableRoot.current) return;
        optionsMutableRoot.current.render(undefined);
        secondaryOptionsMutableRoot.current?.render(undefined);
        if (!prize.prize_type || prize.prize_type === TournamentPrizeType.BADGE_ONLY) {
            return;
        }
        if (prize.prize_type === TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE && secondaryOptionsMutableRoot.current) {
            secondaryOptionsMutableRoot.current.render(buildMonetaryPrizeComponents());
        }
        optionsMutableRoot.current.render(BoolHelper.equalsAny(prize.prize_type, [TournamentPrizeType.PHYSICAL_PRIZE, TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE])
            ? buildPhysicalPrizeComponents()
            : buildMonetaryPrizeComponents());
    }

    if (type === "TOP_WINNER") {
        title = (isNew ? dialogLabels.add_top_winner : dialogLabels.update_top_winner);
    } else {
        title = (isNew ? dialogLabels.add_trophy : dialogLabels.update_trophy);
    }

    const dialog = ConfirmDialog({
        title,
        closable: true,
        style: { minWidth: (width < 500 ? "95%" : 500), maxWidth: "90%" },
        confirmLabel: (isNew ? dialogLabels.add : dialogLabels.update),
        content: (<Form scheme={Schemes.OMNEHOST} style={{ width: "100%", marginTop: 20 }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}>
                <MediaInput manageRef={mediaInputManageRef} style={{ alignSelf: "center", marginBottom: 0 }} width={180} height={180}
                    default={prize.logo_url} noFillTemplate onSelectFiles={(_) => dialogErrorRef.current!.innerText = " "} onGenerateWithAi={async () => {
                        return new File(["We are here"], "we.txt", { type: 'text/plain' });
                    }} />
                <div style={{ flex: 1, marginLeft: 20 }}>
                    <Dropdown fill formControlProps={{ style: { marginTop: 10, visibility: (type === "TOP_WINNER" ? "visible" : "hidden") }, className: "plan-dd", isFieldset: true, label: dialogLabels.position, }}
                        selectedOptionIndex={(prize.position ?? 0) - 1} options={Array(20).fill(null).map((_, index) => ({
                            value: (index + 1),
                            label: Utils.numberWithNth(index + 1)
                        }))} onSelectOption={(o) => {
                            dialogErrorRef.current.innerText = " ";
                            prize.position = o.value;
                        }} />
                    <FormControl isFieldset fill label={dialogLabels.title} style={{ marginTop: 15 }}>
                        <TextInput fill defaultValue={prize.title} onInputComplete={(value: string) => {
                            prize.title = value;
                        }} />
                    </FormControl>
                </div>
            </div>
            <Dropdown fill formControlProps={{ style: { marginTop: 10 }, className: "plan-dd", isFieldset: true, label: dialogLabels.prize_type, }}
                selectedOptionIndex={Object.values(TournamentPrizeType).findIndex((pt) => pt === prize.prize_type)} options={Object.values(TournamentPrizeType).map((p) => ({
                    value: p,
                    label: dialogLabels.prize_type_labels[p.toLowerCase()] ?? Utils.toSaneSentenceFormat(p)
                }))} onSelectOption={(o) => {
                    dialogErrorRef.current.innerText = " ";
                    prize.prize_type = o.value;
                    renderPrizeComponents();
                }} />
            <div ref={(r: any) => {
                if (!r) return;
                ObjectHelper.resolveRef(optionsMutableRoot, ReactDOM.createRoot(r));
                renderPrizeComponents();
            }} />
            <div ref={dialogErrorRef} className="error"> </div>
        </Form>),
        rightStyle: { flex: "unset" },
        rightContent: (<div style={{ height: "100%" }} ref={(r: any) => {
            if (!r) return;
            ObjectHelper.resolveRef(secondaryOptionsMutableRoot, ReactDOM.createRoot(r));
            renderPrizeComponents();
        }} />),
        footerElements: (!isNew ? <Button textOnly scheme={Scheme.DANGER} text={dialogLabels.remove} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
            e.manageRef?.setLoadingState(true);
            consusService.deleteFile([prize.logo_url, prize.physical_prize?.image_url ?? ""]).catch(BaseService.reportError);
            tournamentService.deleteTournamentEditionPrizePool(tournamentEdition.external_id!, prize.external_id).catch(BaseService.reportError).finally(() => {
                e.manageRef?.setLoadingState(false);
                dialog.hide(fetchInformations);
            });
        }} /> : undefined),
        onConfirm: async (b) => {
            if (!prize.type || !prize.prize_type || !prize.title || !(mediaInputManageRef.current?.files()?.length || prize.logo_url) || (prize.type === "TOP_WINNER" && !prize.position)) {
                dialogErrorRef.current.innerText = dialogLabels.missing_fields;
                return false;
            }
            b?.setLoadingState(true);
            function createOrUpdatePrizePool(prize: Partial<ITournamentEditionPrize>, done: (newPrize: ITournamentEditionPrize) => void) {
                const isNew = !prize?.external_id;
                (isNew ? tournamentService.createTournamentEditionPrizePool : tournamentService.updateTournamentEditionPrizePool).bind(tournamentService)(tournamentEdition.external_id!, prize).then(({ sanitized: newPrize }: SanitizedResponse<ITournamentEditionPrize>) => {
                    if (!mediaInputManageRef.current?.files().length) {
                        done(newPrize);
                        return;
                    }
                    consusService.uploadFile(`/omnehost/tournament/prizes/`, `${tournamentEdition.external_id}_${newPrize.external_id!}`, mediaInputManageRef.current?.files()[0]).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        mediaInputManageRef.current.remove();
                        tournamentService.updateTournamentEditionPrizePool(tournamentEdition.external_id!, { ...newPrize, logo_url: sanitized.consus_download_url }).then(({ sanitized: newPrize }: SanitizedResponse<ITournamentEditionPrize>) => {
                            done(newPrize);
                        }).catch(BaseService.reportError);
                    }).catch(BaseService.reportError).finally(() => b?.setLoadingState(false));
                }).catch((err) => {
                    b?.setLoadingState(false);
                    BaseService.reportError(err);
                });
            }
            createOrUpdatePrizePool(prize, (newPrize) => {
                props.setActiveSection?.(CreateTournamentSection.PRIZE_POOL, undefined);
                if (physicalMediaInputManageRef.current?.files().length) {
                    consusService.uploadFile(`/omnehost/tournament/prizes/`, `${tournamentEdition.external_id}_${newPrize.external_id!}_phy`, physicalMediaInputManageRef.current?.files()[0]).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        createOrUpdatePrizePool({ ...newPrize, physical_prize: { ...newPrize.physical_prize as any ?? {}, image_url: sanitized.consus_download_url } }, () => {
                            dialog.hide(fetchInformations);
                        });
                    }).catch(BaseService.reportError).finally(() => b?.setLoadingState(false));
                } else {
                    dialog.hide(fetchInformations);
                }
            });
            return false;
        }
    });
    dialog.show();
}

function PrizePool(props: CreateTournamentSectionProps) {

    const [themeMode, _] = Hooks.useThemeMode();
    const { active, tournamentEditionState } = props;
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.create_tournament.prize_pool;
    const [prizeCountries, setPrizeCountries] = React.useState<string[]>([]);
    const [tournamentEdition, setTournamentEdition] = tournamentEditionState;
    const [tournamentPrizes, setTournamentPrizes] = React.useState<ITournamentEditionPrize[]>();

    React.useEffect(fetchInformations, []);

    React.useEffect(() => {
        setPrizeCountries(Object.keys(tournamentEdition.registration ?? {}).map((currencyCode) => {
            return i18nManager.getCurrencyCountryCode(currencyCode);
        }));
    }, [tournamentEdition.registration]);

    return (<div className="create-tournament-f flow details" ref={props.ref}>
        {!active && (<div className="overlay" onClick={() => props.setActiveSection(CreateTournamentSection.PRIZE_POOL, undefined, true)} />)}
        <span className="title">{labels.title}</span>
        <Form className="content" scheme={Schemes.OMNEHOST} style={{ marginTop: 20, width: "100%" }}>
            <Checkbox label={labels.show_prize_pool_in_listing} defaultChecked={tournamentEdition.extra_data?.show_prize_pool_in_listing} onChecked={(show_prize_pool_in_listing) => {
                setTournamentEdition({ ...tournamentEdition, extra_data: { ...tournamentEdition.extra_data as any, show_prize_pool_in_listing } });
            }} />
            <div style={{ display: "flex", flexDirection: "column", marginTop: 25 }}>
                <p style={{ fontWeight: "bold", fontSize: 16 }}>{labels.top_winners}</p>
                <div className="prize-pool-prizes">
                    <i className="fa fa-plus link" onClick={() => AddOrEditTournamentPrize({
                        prizeCountries,
                        tournamentEdition,
                        fetchInformations,
                        type: "TOP_WINNER",
                        setActiveSection: props.setActiveSection
                    })} />
                    {(tournamentPrizes) ? (tournamentPrizes.filter((p) => p.type === "TOP_WINNER").map((prize) => (<div key={prize.external_id} onClick={() => AddOrEditTournamentPrize({
                        prize,
                        prizeCountries,
                        tournamentEdition,
                        fetchInformations,
                        type: "TOP_WINNER",
                        setActiveSection: props.setActiveSection
                    })}>
                        <span className="text-shadowed">{Utils.numberWithNth(prize.position)}</span>
                        <img alt={prize.title} src={prize.logo_url} />
                    </div>))) : (Elements.LOADING_STATE2)}
                </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", marginTop: 25 }}>
                <p style={{ fontWeight: "bold", fontSize: 16 }}>{labels.trophies}</p>
                <div className="prize-pool-prizes trophies">
                    <i className="fa fa-plus link" onClick={() => AddOrEditTournamentPrize({
                        type: "TROPHY",
                        prizeCountries,
                        tournamentEdition,
                        fetchInformations,
                        setActiveSection: props.setActiveSection
                    })} />
                    {(tournamentPrizes) ? (tournamentPrizes.filter((p) => p.type === "TROPHY").map((prize) => (<div key={prize.external_id} onClick={() => AddOrEditTournamentPrize({
                        prize,
                        type: "TROPHY",
                        prizeCountries,
                        tournamentEdition,
                        fetchInformations,
                        setActiveSection: props.setActiveSection
                    })}>
                        <img alt={prize.title} src={prize.logo_url} />
                        <span className="lined-limit">{prize.title}</span>
                    </div>))) : (Elements.LOADING_STATE2)}
                </div>
            </div>
            <p style={{ fontWeight: "bold", fontSize: 16, marginTop: 25 }}>{labels.pools}</p>
            {prizeCountries.map((prizeCountry) => {
                const countryName = i18nManager.getCountryName(prizeCountry);
                const currency = i18nManager.getCountryCurrency(prizeCountry);
                const isTournamentCountry = prizeCountry === tournamentEdition.country_code;
                const countryPrizePool = tournamentEdition.meta?.price_pool_amounts?.[currency];
                const totalTopWinnerPhysicalPrizes = tournamentPrizes?.
                    filter((p) => BoolHelper.equalsAny(p.prize_type, [TournamentPrizeType.MONETARY_AND_PHYSICAL_PRIZE, TournamentPrizeType.PHYSICAL_PRIZE]))?.
                    filter((p) => p.physical_prize?.available_in_all_countries || isTournamentCountry);

                return (<span key={prizeCountry}>
                    {countryName}{": "}
                    {!countryPrizePool && !totalTopWinnerPhysicalPrizes?.length && (<b>{labels.no_prize}</b>)}
                    {!!countryPrizePool && (<b>{StringHelper.toPresentableMoneyValue(countryPrizePool)} {currency}</b>)}{" "}
                    {!!countryPrizePool && !!totalTopWinnerPhysicalPrizes?.length && (<b>, </b>)}
                    {!!totalTopWinnerPhysicalPrizes?.length && (<b>{totalTopWinnerPhysicalPrizes?.length} {labels.physical_prize}</b>)}
                </span>);
            })}
            <span style={{ color: "#FF7171", marginTop: 50 }}>{labels.note}</span>
        </Form>
        <div className="controls">
            <Button textOnly scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.prev} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.CUSTOM_FIELDS, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
            <Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.next} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.STAGE_AND_FORMAT, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
        </div>
    </div>);

    function fetchInformations() {
        const now = new Date();
        tournamentService.getTournamentEditionPrizePool(tournamentEdition.external_id!).then(({ content }: SanitizedResponse<ITournamentEditionPrize>) => {
            setTournamentPrizes(content.map((c) => {
                c.logo_url = c.logo_url + "?" + now.getTime();
                return c;
            }));
        }).catch(BaseService.reportError);
    }

}

export default PrizePool;
